import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, ArrowRight, CheckCircle, ArrowLeft } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import useAuthStore from '@/lib/stores/authStore';
import { forgotPasswordSchema, type ForgotPasswordFormData } from '@/lib/validations/auth';
import type { FirebaseAuthError } from '@/types';
import { ROUTES } from '@/types';

/**
 * Apple-inspired forgot password form component
 * Handles password reset email sending
 */
export const ForgotPasswordForm: React.FC = () => {
  const [showSuccess, setShowSuccess] = useState(false);
  const { resetPassword, loading, error, clearError } = useAuthStore();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    clearErrors,
    watch,
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    mode: 'onBlur', // Validate when user leaves field
    reValidateMode: 'onChange', // Re-validate on change after first validation
  });

  const watchedFields = watch();
  // Track when fields are being actively edited
  const handleFieldFocus = (fieldName: 'email') => {
    // Clear errors when user focuses on a field (starts typing)
    if (errors[fieldName]) {
      clearErrors(fieldName);
    }
    if (errors.root?.message) {
      clearErrors('root');
    }
    if (error) {
      clearError();
    }
  };

  const onSubmit = async (data: ForgotPasswordFormData) => {
    try {
      clearError();
      await resetPassword(data.email);
      
      // Show success state
      setShowSuccess(true);
    } catch (error: unknown) {
      // Clear store error immediately since we're handling it in the form
      clearError();
      
      // Handle specific Firebase errors
      const firebaseError = error as FirebaseAuthError;
      if (firebaseError.code === 'auth/invalid-email') {
        setError('email', { 
          message: 'Invalid email address' 
        });
      } else {
        setError('root', { 
          message: firebaseError.message || 'Failed to send reset email. Please try again.' 
        });
      }
    }
  };

  // Enable button when email has value and no current validation errors are showing
  const hasValidFormData = watchedFields.email && !errors.email;
  if (showSuccess) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-8"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
          className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4"
        >
          <CheckCircle className="w-8 h-8 text-white" />
        </motion.div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Check Your Email
        </h3>
        <p className="text-gray-600 mb-6">
          We've sent password reset instructions to your email address.
        </p>
        <Link
          to={ROUTES.SIGNIN}
          className="inline-flex items-center text-primary hover:text-primary/80 font-medium transition-colors duration-200"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Sign In
        </Link>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Instructions */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center"
      >
        <p className="text-gray-600 text-sm">
          Enter your email address and we'll send you instructions to reset your password.
        </p>
      </motion.div>

      {/* Form Fields */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Input
          {...register('email')}
          type="email"
          label="Email Address"
          placeholder="your@email.com"
          leftIcon={<Mail className="w-4 h-4" />}
          error={errors.email?.message}
          disabled={isSubmitting || loading}
          autoComplete="email"
          autoFocus
          onFocus={() => handleFieldFocus('email')}
        />
      </motion.div>
      {/* Error Display - Toast style overlay */}
      <AnimatePresence mode="wait">
        {errors.root?.message && (
          <motion.div
            key="error-message"
            initial={{ opacity: 0, scale: 0.95, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
            transition={{ duration: 0.2 }}
            className="bg-red-50 border border-red-200 rounded-lg p-4 shadow-apple-lg"
          >
            <p className="text-sm font-medium text-red-800">
              {errors.root.message}
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Submit Button */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Button
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          loading={isSubmitting || loading}
          rightIcon={!isSubmitting && !loading ? <ArrowRight className="w-4 h-4" /> : undefined}
          disabled={!hasValidFormData || isSubmitting}
        >
          {isSubmitting || loading ? 'Sending...' : 'Send Reset Instructions'}
        </Button>
      </motion.div>

      {/* Back to Login Link */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="text-center pt-4 border-t border-gray-100"
      >
        <Link
          to={ROUTES.SIGNIN}
          className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors duration-200"
        >
          <ArrowLeft className="w-3 h-3 mr-1" />
          Back to Sign In
        </Link>
      </motion.div>
    </form>
  );
};

export default ForgotPasswordForm;